import { useDisclosure } from '@nextui-org/react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import BaseModal from '~/components/modals/base/BaseModal'
import { TaskStatusDetailType, TaskStatuses } from '~/constants/task-status'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { AgentTaskStatus } from '~/protos/agents'
import store, { RootState } from '~/store'
import { setModalParams } from '~/store/modalSlice'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { cm } from '~/utils/utils'

export enum ModalId {
  TaskStatusDetails = 'task-status-details-modal',
  RefactorFileViewer = 'refactor-file-viewer-modal',
  ImageViewer = 'image-viewer-modal',
  FlexTrialModal = 'flex-trial-modal',
  SignupModal = 'signup-modal',
  RedirectModal = 'redirect-modal'
}

interface CommonModalProps<T> {
  id: ModalId
  Component: React.FC<T>
  className?: string
  contentClassName?: string
}

// Type guards for modal props
function hasTaskStatus(props: unknown): props is { taskStatus: AgentTaskStatus } {
  return props !== null && typeof props === 'object' && 'taskStatus' in props
}

function hasTitle(props: unknown): props is { title: string } {
  return props !== null && typeof props === 'object' && 'title' in props
}

export function CommonModal<T extends Record<string, unknown>>({
  Component,
  id,
  className,
  contentClassName
}: CommonModalProps<T>) {
  const isCompactViewport = useCompactViewport()
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure()
  const props = useSelector((state: RootState) => state.modals.modalParams[id])
  const taskStatus = hasTaskStatus(props) ? props.taskStatus : null
  const directModalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOpen = ({ id: modalId, params }: { id: ModalId; params: T }) => {
      if (modalId === id) {
        store.dispatch(setModalParams({ id, params }))
        onOpen()
      }
    }
    const handleClose = ({ id: modalId }: { id: ModalId }) => {
      if (modalId === id) {
        onClose()
      }
    }

    globalEventEmitter.on(AppEvents.OpenModal, handleOpen)
    globalEventEmitter.on(AppEvents.CloseModal, handleClose)

    return () => {
      globalEventEmitter.off(AppEvents.OpenModal, handleOpen)
      globalEventEmitter.off(AppEvents.CloseModal, handleClose)
    }
  }, [id, onOpen, onClose])

  // Check if this is an endpoint selection modal that should skip the wrapper
  const isEndpointSelection =
    taskStatus !== null &&
    TaskStatuses[taskStatus]?.detailType === TaskStatusDetailType.ENDPOINT_SELECTION

  // Check if this is a diff detail modal (needs more height in compact/plugin view)
  const isDiffDetail =
    taskStatus !== null && TaskStatuses[taskStatus]?.detailType === TaskStatusDetailType.DIFF

  // Check if this is a risk-review detail modal (split-pane layout, full screen)
  const isRiskReview =
    taskStatus !== null && TaskStatuses[taskStatus]?.detailType === TaskStatusDetailType.RISK_REVIEW

  // Check if this is a refactor modal that should also skip the wrapper
  const isRefactorModal = id === ModalId.RefactorFileViewer || id === ModalId.ImageViewer
  const shouldRenderDirectModal = (isEndpointSelection || isRiskReview || isRefactorModal) && isOpen

  useEffect(() => {
    if (shouldRenderDirectModal) {
      directModalRef.current?.focus()
    }
  }, [shouldRenderDirectModal])

  // For endpoint selection, risk review, and refactor modals, render the component directly without BaseModal wrapper
  if (shouldRenderDirectModal) {
    return (
      <div
        ref={directModalRef}
        className='fixed inset-0 z-50 flex items-center justify-center bg-black/45'
        tabIndex={-1}
        onKeyDown={event => {
          if (event.key === 'Escape') onClose()
        }}
      >
        <div className='bg-white rounded-xl shadow-2xl max-w-[95vw] max-h-[95vh] overflow-auto'>
          <Component {...(props as T)} />
        </div>
      </div>
    )
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={hasTitle(props) ? props.title : null}
      className={cm(
        'absolute shadow-heavy max-h-[90vh]',
        isCompactViewport
          ? cm(
              'w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] min-w-0',
              isDiffDetail ? 'h-[calc(100vh-32px)]' : 'h-fit'
            )
          : isDiffDetail
            ? 'w-[92vw] max-w-[1600px] h-fit'
            : 'min-w-[400px] max-w-[85vw] w-fit h-fit'
      )}
      headerBorder={false}
      bodyClassName='p-0'
      wrapperClassName={className}
      contentClassName={contentClassName}
    >
      <Component {...(props as unknown as T)} />
    </BaseModal>
  )
}

export function useModalController<T>(id: ModalId) {
  const openModal = useCallback(
    (params: T) => {
      globalEventEmitter.emit(AppEvents.OpenModal, { id, params })
    },
    [id]
  )
  const closeModal = useCallback(() => {
    globalEventEmitter.emit(AppEvents.CloseModal, { id })
  }, [id])
  return useMemo(() => ({ openModal, closeModal }), [openModal, closeModal])
}
